import React, { useContext, useEffect, useRef, useState } from "react"
import { StyleSheet, Text, View } from "react-native"
import { connect } from "react-redux"
import { useIsFocused } from "@react-navigation/native"
import * as amplitude from "@amplitude/analytics-react-native"
import {
    FUNNEL_TITLE_FONT_SIZE,
    FUNNEL_TITLE_LINE_HEIGHT,
    WIDE_BREAKPOINT,
} from "@common/components/funnel/FunnelQuestionScreen"
import FunnelStepHeader from "@common/components/funnel/FunnelStepHeader"
import FunnelStepLayout from "@common/components/funnel/FunnelStepLayout"
import FunnelToFix from "@common/components/funnel/FunnelToFix"
import TextButtonM from "@common/components/button/TextButtonM"
import { FUNNEL_SCENES } from "@common/components/funnel/funnelScenes"
import { theme } from "@common/theme/theme"
import { elide } from "@common/utils/activationSuiteDisplay"
import { DimensionsContext } from "@common/utils/mobileUtils"
import CommitmentSwitch from "./components/CommitmentSwitch"
import EditLink from "./components/EditLink"
import { FormatSession } from "./components/FormatInfoSheet"
import FormatTiles from "./components/FormatTiles"
import HourBar from "./components/HourBar"
import FormulaFooter from "./components/FormulaFooter"
import {
    ReservationCard,
    ReservationSetting,
} from "./checkout/ReservationBlocks"
import {
    INSCRIPTION_LYCEE_RULE,
    INSCRIPTION_STEPS,
} from "./constants/inscriptionContent"
import {
    COMMITMENT_COPY,
    DEFAULT_OFFER_TYPE,
    INSCRIPTION_FORMATS,
    formatNameOfOffer,
} from "./constants/inscriptionFormats"
import { useInscriptionFunnel } from "./context/InscriptionFunnelContext"
import { useFormulaChoice } from "./hooks/useFormulaChoice"
import { useFormulaChoiceSave } from "./hooks/useFormulaChoiceSave"
import { useInscriptionFunnelGuard } from "./hooks/useInscriptionFunnelGuard"
import { useInscriptionLead } from "./hooks/useInscriptionLead"
import {
    formatInfoCopy,
    formatSessions,
    formatTiles,
} from "./utils/formatSessions"
import { pickFormat, toggleCommitment } from "./utils/formulaChoice"
import { formulaGap } from "./utils/subjectsFitHours"

const DARK = theme.colors.primaryDark || theme.colors.primary

/** Amène un message dans la zone visible, sans bouger la page s'il s'y lit déjà. */
const reveal = (ref) =>
    ref.current?.scrollIntoView?.({ block: "nearest", behavior: "smooth" })

// Ce qu'un geste demandait et que la formule ne porte pas : la page le dit,
// rien n'est écrit, et le parent le règle à l'étape formule.
const REFUSALS = {
    hours: (next) =>
        `${formatNameOfOffer(next.offerType)} ne se prend pas à ce volume. Changez d'abord vos heures.`,
    subjects: () => `${INSCRIPTION_LYCEE_RULE} Ajustez d'abord vos matières.`,
    slots: (next) =>
        `Il faut autant de créneaux que d'heures\u00a0: ${next.weeklyHours} pour cette formule. Ajoutez-en d'abord.`,
}

/**
 * Étape « Format de cours », quand elle a sa propre page (dossiers
 * tirés `FORMAT_STEP.ownStep`) : les deux formats en tête, côte à côte, la
 * formule juste dessous, puis ce que l'élève vit dans le format choisi. Le
 * prix est dans le bouton.
 *
 * Le choix obéit aux règles de l'étape formule (`pickFormat`,
 * `toggleCommitment`) : mêmes prix, même sans engagement, même verrou sur
 * Solo. Les heures se règlent à l'étape d'avant — la ligne des heures y
 * ramène — et un choix que la formule ne porte pas (`formulaGap`) aussi.
 */
const InscriptionFormatScreen = ({
    navigation,
    noCommitment,
    priceGrid,
    weeklyHoursByOffer,
}) => {
    const windowWidth = useContext(DimensionsContext)?.width ?? 0
    const { state } = useInscriptionFunnel()
    const { nextParams } = useInscriptionFunnelGuard()
    const { funnelStateId } = useInscriptionLead()
    const { save, saving, error } = useFormulaChoiceSave()
    // `{ at, message, nudge }` : `at` dit sous quel geste il s'affiche, `nudge`
    // le fait trembler à chaque nouveau refus.
    const [refusal, setRefusal] = useState(null)
    // Un refus ou un enregistrement raté se dit sous le geste : s'il tombe
    // sous le pied de page, la page remonte juste ce qu'il faut pour le
    // montrer.
    const refusalRef = useRef(null)
    const errorRef = useRef(null)
    useEffect(() => {
        if (refusal) reveal(refusalRef)
    }, [refusal])
    useEffect(() => {
        if (error) reveal(errorRef)
    }, [error])

    const firstname = state.studentFirstname || "votre enfant"
    const quote = state.quote || null
    const { choice, rules, commitment, trackCommitment } = useFormulaChoice({
        formula: {
            offerType: state.offerType || DEFAULT_OFFER_TYPE,
            weeklyHours: state.weeklyHours || 0,
            hoursAllocation: state.hoursAllocation || {},
        },
        subjectIds: (state.subjects || []).map(String),
        served: { noCommitment, priceGrid, weeklyHoursByOffer },
        saving,
        quote,
        source: "format",
    })

    // L'étape formule est juste dessous : on y REVIENT. `navigate` en
    // empilerait une seconde, qui redemanderait son devis en se montant.
    const backToFormula = () =>
        navigation.canGoBack()
            ? navigation.goBack()
            : navigation.navigate("inscription/configuration", nextParams)

    // La page agit quand elle est DEVANT le parent : une reprise par lien la
    // monte sous l'écran des masteurs, où elle ne doit ni renvoyer en arrière,
    // ni redemander un devis, ni se compter vue.
    const isFocused = useIsFocused()
    const viewed = useRef(false)
    useEffect(() => {
        if (!isFocused) return
        // Le format se choisit sur une formule déjà composée : sans volume,
        // c'est à l'étape d'avant que le parcours reprend.
        if (!choice.weeklyHours) {
            backToFormula()
            return
        }
        // Un devis qui ne répond plus aux réponses (page rechargée avant le
        // retour d'un enregistrement) se redemande.
        if (!quote || state.quoteStale) save(choice)
        if (viewed.current) return
        viewed.current = true
        amplitude.track("inscription_funnel_format_step_viewed", {
            funnel_state_id: funnelStateId,
        })
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isFocused])

    const tiles = formatTiles({ choice, rules })
    const tile = tiles.find(({ selected }) => selected) ?? tiles[0]
    const session = formatSessions(firstname).find(
        ({ key }) => key === tile.key
    )
    const copy = formatInfoCopy(firstname)

    const choose = (next, at) => {
        const gap = formulaGap({
            studentClass: state.studentClass,
            subjectCount: rules.subjectIds.length,
            slotCount: (state.slots || []).length,
            weeklyHours: next.weeklyHours,
        })
        setRefusal((previous) =>
            gap
                ? {
                      at,
                      message: REFUSALS[gap](next),
                      nudge: (previous?.nudge ?? 0) + 1,
                  }
                : null
        )
        if (!gap) save(next)
        return !gap
    }

    const onOpenFormat = (key) => {
        const next = pickFormat(
            choice,
            INSCRIPTION_FORMATS[key].offerType,
            rules
        )
        if (next === choice) return
        if (!choose(next, "format")) return
        amplitude.track("inscription_funnel_format_selected", {
            funnel_state_id: funnelStateId,
            offer_type: next.offerType,
            source: "format",
        })
        if (next.withCommitment !== choice.withCommitment) {
            trackCommitment(next.withCommitment)
        }
    }

    const onToggleCommitment = () => {
        const next = toggleCommitment(choice, rules)
        if (!choose(next, "commitment")) return
        trackCommitment(next.withCommitment)
    }

    const goNext = () => {
        amplitude.track("inscription_funnel_format_step_completed", {
            funnel_state_id: funnelStateId,
            offer_type: choice.offerType,
        })
        navigation.navigate("inscription/tutors", nextParams)
    }

    // Le devis ne compte que s'il répond au choix affiché : pendant qu'un
    // choix s'enregistre, ou après un enregistrement raté, le bouton
    // n'annonce pas de prix.
    const priced = !!quote && !saving && !state.quoteStale

    const refusalAt = (at) =>
        refusal?.at === at ? (
            <View ref={refusalRef} style={styles.notice}>
                <View style={styles.toFix}>
                    <FunnelToFix
                        message={refusal.message}
                        nudge={refusal.nudge}
                    />
                </View>
                <EditLink label="Formule" onPress={backToFormula} />
            </View>
        ) : null

    return (
        <FunnelStepLayout
            // Rien au-dessus du choix sur un téléphone : les formats doivent
            // ouvrir la page. La scène reste aux grands écrans.
            scene={
                windowWidth >= WIDE_BREAKPOINT
                    ? FUNNEL_SCENES.composition
                    : null
            }
            contentContainerStyle={styles.content}
            header={
                <FunnelStepHeader
                    step={INSCRIPTION_STEPS.configuration}
                    total={INSCRIPTION_STEPS.total}
                    onBack={() => navigation.goBack()}
                />
            }
            footer={
                <FormulaFooter
                    // La carte des heures et de l'engagement, sous les tuiles,
                    // dit déjà le volume et l'engagement : le pied ne garde
                    // que le prix.
                    recap={false}
                    quote={priced ? quote : null}
                    disabled={!priced}
                    onPress={goNext}
                />
            }
        >
            <View style={styles.body}>
                {/* 1 — le choix : les deux formats côte à côte, comme à
                    l'étape formule — même tuile, même barre, mêmes prix. */}
                <Text style={styles.title}>
                    Le format de cours {elide(firstname)}
                </Text>
                <FormatTiles
                    tiles={tiles}
                    lockedLabel={COMMITMENT_COPY.lockedTile}
                    onPick={onOpenFormat}
                />

                {refusalAt("format")}

                {/* 2 — ce qui se vend, juste sous les tuiles : couper
                    l'engagement change le format sous les yeux du parent. */}
                <ReservationCard>
                    <View style={styles.card}>
                        <ReservationSetting
                            label="Heures"
                            value={`${choice.weeklyHours}\u00a0h par semaine`}
                            onEdit={backToFormula}
                        />
                        {commitment ? (
                            <View style={styles.cardRow}>
                                <CommitmentSwitch
                                    label={COMMITMENT_COPY.switchLabel}
                                    savingLabel={commitment.savingLabel}
                                    checked={commitment.checked}
                                    pending={commitment.pending}
                                    onToggle={onToggleCommitment}
                                />
                            </View>
                        ) : null}
                        {refusalAt("commitment")}
                    </View>
                </ReservationCard>

                {error ? (
                    <View ref={errorRef} style={styles.notice}>
                        <Text style={styles.error}>{error}</Text>
                        <TextButtonM
                            label="Réessayer"
                            onPress={() => save(choice)}
                            fontWeight="Bold"
                            style={styles.retry}
                        />
                    </View>
                ) : null}

                {/* 3 — ce que l'élève y vit, pour le format choisi : son nom
                    dans le titre, puis la barre de son heure, légende des
                    deux temps. */}
                <View style={styles.zone}>
                    <Text style={styles.zoneTitle}>
                        {copy.titleIn(firstname, session.name)}
                    </Text>
                    <HourBar visits={session.visits} alt={session.alt} />
                    <FormatSession format={session} copy={copy} />
                </View>
            </View>
        </FunnelStepLayout>
    )
}

const styles = StyleSheet.create({
    content: { paddingBottom: 24 },
    // Plus d'air entre les zones qu'à l'intérieur : c'est lui qui les sépare.
    body: { gap: 26 },
    title: {
        fontFamily: "Montserrat-ExtraBold",
        fontSize: FUNNEL_TITLE_FONT_SIZE,
        lineHeight: FUNNEL_TITLE_LINE_HEIGHT,
        color: DARK,
    },
    zone: { gap: 12 },
    zoneTitle: {
        fontFamily: "Montserrat-ExtraBold",
        fontSize: 16,
        lineHeight: 21,
        color: DARK,
    },
    card: { gap: 10, paddingVertical: 12, paddingHorizontal: 12 },
    cardRow: {
        paddingTop: 10,
        borderTopWidth: 1,
        borderTopColor: theme.colors.lightMore,
    },
    notice: { alignItems: "center", gap: 4 },
    toFix: { alignSelf: "stretch", marginBottom: -8 },
    retry: { fontSize: 13, paddingVertical: 4 },
    error: {
        fontFamily: "Montserrat-Medium",
        fontSize: 13,
        lineHeight: 18,
        color: theme.colors.error,
        textAlign: "center",
    },
})

const mapStateToProps = (state) => ({
    noCommitment: state.data?.inscriptionFunnel?.noCommitment,
    priceGrid: state.data?.inscriptionFunnel?.priceGrid,
    weeklyHoursByOffer: state.data?.inscriptionFunnel?.weeklyHoursByOffer,
})

export default connect(mapStateToProps)(InscriptionFormatScreen)
